import React from "react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ComponentTitle, PageTitleLastCol } from "../custom-components";
import { Alert, Button, Card, Col, message, Row, Select, Space, Statistic, Tag, Tabs } from "antd";
import { authAxios } from "../utils/session_utils";
import moment from "moment";
import { AxiosRequestConfig } from "axios";
import { PlanWarning } from "./utils/batchJobWarnings";
import { getFilledLengthForSlot } from "./utils/slotTableUtils";
import { formatIdShort } from "../utils";
import { InventorySummaryComponent } from "./InventorySummaryComponent";
import SlotTableCore from "./components/SlotTableCore";
import {
    AutopodderResultRow,
    AutopodderResultsResponse,
    ScheduleDataItem,
    ScheduleDataResponse,
    FinalSlotSelectionDict,
} from "./types";
import { getAdSelectionCounts } from "./utils/slotTableUtils";

const BatchJobsPoddedReviewComponent = () => {
    const location = useLocation();
    const [batchJobId, setBatchJobId] = useState<string | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setBatchJobId(params.get("batchJobId"));
    }, [location.search]);

    const [poddedData, setPoddedData] = useState<AutopodderResultRow[]>([]);
    const [scheduleData, setScheduleData] = useState<{ [key: string]: ScheduleDataItem }>({});
    const [loadingCount, setLoadingCount] = useState<number>(1); // starts at 1 for initial load
    const loading = loadingCount > 0;
    const startLoading = () => setLoadingCount(c => c + 1);
    const stopLoading = () => setLoadingCount(c => Math.max(0, c - 1));
    const [slotSelectionDict, setSlotSelectionDict] = useState<{ [key: string]: string[] }>({});
    const [warnings, setWarnings] = useState<PlanWarning[]>([]);

    // Multi-strategy support
    const [availableStrategies, setAvailableStrategies] = useState<string[]>([]);
    const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
    const [allPoddedData, setAllPoddedData] = useState<AutopodderResultRow[]>([]);
    const [allWarnings, setAllWarnings] = useState<{ [strategy: string]: PlanWarning[] }>({});
    const [strategyMetrics, setStrategyMetrics] = useState<{ [strategy: string]: any }>({});

    // Track original autopodder selections per strategy for accurate inventory calculation
    const [originalSelectionsByStrategy, setOriginalSelectionsByStrategy] = useState<{ [strategy: string]: { [slotId: string]: string[] } }>({});

    // Master SCHEDULE_NAME -> ISCI_CODE map, populated from the initial unfiltered inventory fetch.
    const [isciByAdName, setIsciByAdName] = useState<{ [adName: string]: string }>({});

    const [duplicateScheduleNames, setDuplicateScheduleNames] = useState<string[]>([]);
    const [lockedDays, setLockedDays] = useState<string[]>([]);
    const [daysInOriginal, setDaysInOriginal] = useState<string[]>([]);
    const [daysInUpdated, setDaysInUpdated] = useState<string[]>([]);
    const [noTrafficExcludedDays, setNoTrafficExcludedDays] = useState<string[]>([]);


    useEffect(() => {
        const fetchPoddedData = async () => {
            if (batchJobId) {
                try {

                    const url = `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/autopodder-results?batchJobId=${batchJobId}`;
                    const config: AxiosRequestConfig = {
                        method: "GET",
                        url,
                        headers: {
                            "Content-Type": "application/json",
                        },
                    };
                    const response = await authAxios(config);
                    const result = response.data as AutopodderResultsResponse;
                    console.log("Fetched autopodder results:", {
                        totalRows: result.rows?.length || 0,
                        hasError: !!result.err
                    });

                    if (result.err) {
                        console.error("API returned error:", result.err);
                    }

                    // Extract unique strategies from the data
                    const strategies = Array.from(new Set(result.rows.map(row => row.STRATEGY_NAME))).filter(Boolean);
                    console.log("Extracted strategies:", strategies);
                    setAvailableStrategies(strategies);

                    // Set default strategy (first one available) and populate
                    // poddedData in the same render batch to avoid a flash of
                    // "No slot data found" before the strategy-filter useEffect fires.
                    if (strategies.length > 0 && !selectedStrategy) {
                        setSelectedStrategy(strategies[0]);
                        const filtered = (result.rows || []).filter(row => row.STRATEGY_NAME === strategies[0]);
                        const sorted = [...filtered].sort((a, b) => (a.FILLORDER || 0) - (b.FILLORDER || 0));
                        setPoddedData(sorted);
                    } else if (strategies.length === 0 && result.rows.length > 0) {
                        // No strategy names found in data - show all data without filtering
                        setPoddedData(result.rows || []);
                    }

                    // Store all data
                    setAllPoddedData(result.rows || []);

                    // Initialize slot selection dict for all rows and track original selections by strategy
                    const initialSlotDict: { [key: string]: string[] } = {};
                    const originalSelections: { [strategy: string]: { [slotId: string]: string[] } } = {};

                    for (const row of result.rows) {
                        // Handle empty or null SELECTED_AD_COMBO_ADS_STR
                        const adNamesStr = row.SELECTED_AD_COMBO_ADS_STR || '';
                        const adNames = adNamesStr.split(',').map((name: string) => name.trim());
                        // clear any empty strings
                        const filteredAdNames = adNames.filter((name: string) => name.length > 0);
                        initialSlotDict[row.ID] = filteredAdNames;

                        // Store original selections grouped by strategy
                        const strategy = row.STRATEGY_NAME || 'unknown';
                        if (!originalSelections[strategy]) {
                            originalSelections[strategy] = {};
                        }
                        originalSelections[strategy][row.ID] = [...filteredAdNames];
                    }
                    setSlotSelectionDict(initialSlotDict);
                    setOriginalSelectionsByStrategy(originalSelections);
                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            }
        };

        const fetchScheduleData = async (strategyFilter?: string) => {
            if (batchJobId) {
                try {
                    let url = `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/autopodder-ad-inventory?batchJobId=${batchJobId}`;

                    // Add strategy filter if provided
                    if (strategyFilter) {
                        url += `&strategyName=${encodeURIComponent(strategyFilter)}`;
                    }

                    const config: AxiosRequestConfig = {
                        method: "GET",
                        url,
                        headers: {
                            "Content-Type": "application/json",
                        },
                    };
                    const response = await authAxios(config);
                    const result = response.data as ScheduleDataResponse;
                    console.log("Fetched schedule data:", result);
                    console.log("First row sample:", result.rows[0]);
                    const schedulesDict: { [key: string]: ScheduleDataItem } = {};
                    const isciDict: { [key: string]: string } = {};
                    const duplicates = new Set<string>();
                    result.rows.forEach((item: ScheduleDataItem) => {
                        if (strategyFilter && schedulesDict[item.SCHEDULE_NAME]) {
                            duplicates.add(item.SCHEDULE_NAME);
                        }
                        schedulesDict[item.SCHEDULE_NAME] = item;
                        isciDict[item.SCHEDULE_NAME] = item.ISCI_CODE || '';
                    });
                    setDuplicateScheduleNames(strategyFilter ? Array.from(duplicates) : []);
                    console.log("ScheduleData dict size:", Object.keys(schedulesDict).length);
                    setScheduleData(schedulesDict);
                    // Only seed the master ISCI map from the unfiltered fetch so
                    // we keep coverage for ads in non-selected strategies.
                    if (!strategyFilter) {
                        setIsciByAdName(isciDict);
                    }
                } catch (error) {
                    console.error("Error fetching schedule data:", error);
                }
            }
        };
        const fetchBatchJobData = async () => {
            if (!batchJobId) {
                return;
            }
            try {
                // Fetch single batch job by ID
                const url = `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/${batchJobId}`;
                const config: AxiosRequestConfig = {
                    method: "GET",
                    url,
                    headers: {
                        "Content-Type": "application/json",
                    },
                };
                const response = await authAxios(config);
                const currentJob = response.data?.data;

                if (currentJob?.job_result_json) {
                    // Parse the JSON string first (same as listing view does)
                    const json_result = JSON.parse(currentJob.job_result_json);

                    // Extract warnings and metrics per strategy
                    const warningsByStrategy: { [strategy: string]: PlanWarning[] } = {};
                    const metricsByStrategy: { [strategy: string]: any } = {};

                    // Handle multi-strategy format: json_result.strategies
                    if (json_result.strategies) {
                        for (const strategyName in json_result.strategies) {
                            const strategyData = json_result.strategies[strategyName];
                            if (strategyData.warnings && Array.isArray(strategyData.warnings)) {
                                warningsByStrategy[strategyName] = strategyData.warnings;
                            }
                            // Extract key metrics
                            metricsByStrategy[strategyName] = {
                                overallFillRate: strategyData.overallFillRate,
                                totalSlots: strategyData.totalSlots,
                                totalUnfilledTime: strategyData.totalUnfilledTime,
                                totalDollars: strategyData.totalDollars,
                                fullyFilledSlots: strategyData.fullyFilledSlots,
                                unfilledSlots: strategyData.unfilledSlots?.length || 0,
                                durationSeconds: strategyData.durationSeconds
                            };
                        }
                    }
                    // Handle broadcast format: json_result.data
                    else if (json_result.data) {
                        for (const strategyName in json_result.data) {
                            const strategyData = json_result.data[strategyName];
                            if (strategyData.warnings && Array.isArray(strategyData.warnings)) {
                                warningsByStrategy[strategyName] = strategyData.warnings;
                            }
                            // Extract key metrics (broadcast format may differ)
                            metricsByStrategy[strategyName] = {
                                overallFillRate: strategyData.overallFillRate,
                                totalSlots: strategyData.totalSlots,
                                totalUnfilledTime: strategyData.totalUnfilledTime || strategyData.unfilledTime,
                                totalDollars: strategyData.totalDollars,
                                fullyFilledSlots: strategyData.fullyFilledSlots,
                                unfilledSlots: strategyData.unfilledSlots?.length || 0,
                                durationSeconds: strategyData.durationSeconds
                            };
                        }
                    }

                    setAllWarnings(warningsByStrategy);
                    setStrategyMetrics(metricsByStrategy);
                }

                // Extract progressive podding metadata from processing_info_json
                if (currentJob?.processing_info_json) {
                    try {
                        const processingInfo = JSON.parse(currentJob.processing_info_json);
                        if (Array.isArray(processingInfo.lockedDays)) setLockedDays(processingInfo.lockedDays);
                        if (Array.isArray(processingInfo.daysInOriginal)) setDaysInOriginal(processingInfo.daysInOriginal);
                        if (Array.isArray(processingInfo.daysInUpdated)) setDaysInUpdated(processingInfo.daysInUpdated);
                        if (Array.isArray(processingInfo.noTrafficExcludedDays)) setNoTrafficExcludedDays(processingInfo.noTrafficExcludedDays);
                    } catch (e) {
                        console.error("Error parsing processing_info_json:", e);
                    }
                }
            } catch (error) {
                console.error("Error fetching batch job data:", error);
            }
        };

        Promise.all([fetchPoddedData(), fetchScheduleData(), fetchBatchJobData()])
            .finally(() => stopLoading());
    }, [batchJobId]);

    // Filter podded data when selected strategy changes
    useEffect(() => {
        if (selectedStrategy && allPoddedData.length > 0) {
            const filteredData = allPoddedData.filter(row => row.STRATEGY_NAME === selectedStrategy);
            // Sort by FILLORDER ascending if available
            const sortedData = [...filteredData].sort((a, b) => {
                const aOrder = a.FILLORDER || 0;
                const bOrder = b.FILLORDER || 0;
                return aOrder - bOrder;
            });
            setPoddedData(sortedData);
        } else if (!selectedStrategy && allPoddedData.length > 0 && availableStrategies.length === 0) {
            // No strategies defined - show all data
            setPoddedData(allPoddedData);
        }
    }, [selectedStrategy, allPoddedData, availableStrategies]);

    // Update warnings when selected strategy changes
    useEffect(() => {
        if (selectedStrategy && allWarnings[selectedStrategy]) {
            setWarnings(allWarnings[selectedStrategy]);
        } else {
            setWarnings([]);
        }
    }, [selectedStrategy, allWarnings]);

    // Refetch schedule data when selected strategy changes
    useEffect(() => {
        const fetchScheduleData = async (strategyFilter?: string) => {
            if (batchJobId) {
                startLoading();
                try {
                    let url = `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/autopodder-ad-inventory?batchJobId=${batchJobId}`;

                    // Add strategy filter if provided
                    if (strategyFilter) {
                        url += `&strategyName=${encodeURIComponent(strategyFilter)}`;
                    }

                    const config: AxiosRequestConfig = {
                        method: "GET",
                        url,
                        headers: {
                            "Content-Type": "application/json",
                        },
                    };
                    const response = await authAxios(config);
                    const result = response.data as ScheduleDataResponse;
                    console.log("Fetched schedule data for strategy:", strategyFilter || 'all');
                    const schedulesDict: { [key: string]: ScheduleDataItem } = {};
                    const duplicates = new Set<string>();
                    result.rows.forEach((item: ScheduleDataItem) => {
                        if (schedulesDict[item.SCHEDULE_NAME]) {
                            duplicates.add(item.SCHEDULE_NAME);
                        }
                        schedulesDict[item.SCHEDULE_NAME] = item;
                    });
                    setDuplicateScheduleNames(Array.from(duplicates));
                    setScheduleData(schedulesDict);
                } catch (error) {
                    console.error("Error fetching schedule data:", error);
                } finally {
                    stopLoading();
                }
            }
        };

        if (selectedStrategy) {
            fetchScheduleData(selectedStrategy);
        }
    }, [selectedStrategy, batchJobId]);

    // Calculate how many times each ad was originally selected by autopodder (only for current strategy)
    const getOriginalAdSelectionCounts = (): { [scheduleName: string]: number } => {
        const counts: { [scheduleName: string]: number } = {};

        if (!selectedStrategy) return counts;

        const originalSelections = originalSelectionsByStrategy[selectedStrategy] || {};

        Object.entries(originalSelections).forEach(([, adNames]) => {
            adNames.forEach((adName) => {
                counts[adName] = (counts[adName] || 0) + 1;
            });
        });
        return counts;
    };

    // Count distinct podded ads for a strategy that have no ISCI on file.
    // Returns { missing, total } so the UI can render "missing/total".
    const getMissingTrafficForStrategy = (strategy: string): { missing: number; total: number } => {
        const selections = originalSelectionsByStrategy[strategy] || {};
        const seen = new Set<string>();
        const missing = new Set<string>();
        Object.values(selections).forEach(adNames => {
            adNames.forEach(adName => {
                seen.add(adName);
                if (!isciByAdName[adName]) {
                    missing.add(adName);
                }
            });
        });
        return { missing: missing.size, total: seen.size };
    };

    const getSlotById = (slotId: string): AutopodderResultRow | null => {
        const slot = poddedData.find((item) => item.ID === slotId);
        return slot || null;
    };

    // Debug logging (can be removed after testing)
    if (process.env.NODE_ENV === 'development') {
        console.log("BatchJobsPoddedReview:", {
            batchJobId,
            loading,
            availableStrategies: availableStrategies.length,
            selectedStrategy,
            allDataRows: allPoddedData.length,
            filteredDataRows: poddedData.length,
            scheduleAds: Object.keys(scheduleData).length
        });
    }

    return (
        <>
            <style>{`
                .ant-tabs-tab:not(.ant-tabs-tab-active) .ant-tabs-tab-btn {
                    color: rgba(255, 255, 255, 0.85) !important;
                }
                .ant-tabs-tab:not(.ant-tabs-tab-active):hover .ant-tabs-tab-btn {
                    color: rgba(255, 255, 255, 1) !important;
                }
            `}</style>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
                <Row justify="space-between" align="middle">
                    <Col>
                        <ComponentTitle>
                            Poded Results: {formatIdShort(batchJobId)}
                            {selectedStrategy && availableStrategies.length > 1 && (
                                <Tag
                                    color="blue"
                                    style={{
                                        marginLeft: 16,
                                        fontSize: '14px',
                                        padding: '4px 12px',
                                        verticalAlign: 'middle'
                                    }}
                                >
                                    {selectedStrategy}
                                </Tag>
                            )}
                        </ComponentTitle>
                    </Col>
                    <PageTitleLastCol>
                        {availableStrategies.length > 1 && (
                            <>
                                <span style={{ marginRight: 8, color: 'rgba(255, 255, 255, 0.85)' }}>Strategy:</span>
                                <Select
                                    value={selectedStrategy}
                                    onChange={setSelectedStrategy}
                                    style={{ width: 150, marginRight: 16 }}
                                    size="large"
                                >
                                    {availableStrategies.map(strategy => (
                                        <Select.Option key={strategy} value={strategy}>
                                            {strategy}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </>
                        )}
                        <Button
                            loading={loading}
                            disabled={loading}
                            type="primary"
                            onClick={() => {
                                startLoading();
                                const finalSlotSelectionDict: FinalSlotSelectionDict = {};
                                for (const slotId in slotSelectionDict) {
                                    // only include non-empty selections
                                    if (slotSelectionDict[slotId].length > 0) {
                                        const slot = getSlotById(slotId);
                                        const slotDateStr = slot ? moment(slot.DATE_TIME).format("YYYY-MM-DD") : "unknown date";
                                        finalSlotSelectionDict[slotId] = {
                                            slotSize: slot ? slot.SIZE_MAX : 0,
                                            slotDateStr: slotDateStr,
                                            podding: slotSelectionDict[slotId],
                                            poddingLength: getFilledLengthForSlot(slotId, slotSelectionDict, scheduleData)
                                        };
                                    }
                                }
                                const savePoddedSelections = async () => {
                                    if (batchJobId) {
                                        try {
                                            const url = `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/autopodder/save-podded-selections`;
                                            const config: AxiosRequestConfig = {
                                                method: "POST",
                                                url,
                                                headers: {
                                                    "Content-Type": "application/json",
                                                },
                                                data: {
                                                    batchJobId,
                                                    slotSelectionDict: finalSlotSelectionDict
                                                }
                                            };
                                            const response = await authAxios(config);
                                            const result = response.data as { err?: string };
                                            if (result.err) {
                                                message.error("Error saving podded selections: " + result.err);
                                            } else {
                                                message.success("Podded selections saved successfully");
                                                // redirect to batch jobs page
                                                window.location.href = '/batch-jobs';
                                            }
                                        } catch (error) {
                                            console.error("Error saving podded selections:", error);
                                            message.error("Error saving podded selections");
                                        } finally {
                                            stopLoading();
                                        }
                                    }
                                };
                                savePoddedSelections();
                            }}> {loading ? 'Please wait...' : 'Save Selections'} </Button>
                        &nbsp;
                        <Button
                            type="default"
                            onClick={() => {
                                window.location.href = '/batch-jobs'; // Redirect to the batch jobs page
                            }}
                        >
                            Cancel
                        </Button>

                    </PageTitleLastCol>
                </Row>
                {duplicateScheduleNames.length > 0 && (
                    <Alert
                        type="warning"
                        message="Duplicate schedule names in inventory"
                        description={`The following schedule names appear more than once; this could lead to inconsistent results: ${duplicateScheduleNames.join(', ')}`}
                        showIcon
                    />
                )}
                {lockedDays.length > 0 && (
                    <Alert
                        type="info"
                        showIcon
                        message="Progressive Podding Active"
                        description={
                            <span>
                                <strong>Locked days:</strong> {lockedDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                                {' '}&mdash;{' '}these days were in the original template ({daysInOriginal.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')})
                                {' '}but removed from this run ({daysInUpdated.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}).
                                {' '}Ads with matching Days of Air had their units reduced accordingly.
                            </span>
                        }
                    />
                )}
                {noTrafficExcludedDays.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message="No-Traffic Ads Excluded"
                        description={
                            <span>
                                <strong>Excluded on:</strong> {noTrafficExcludedDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                                {' '}&mdash;{' '}on these days, ads with no traffic (no ISCI on file) were not eligible for podding.
                                {' '}Check the Rejection Reasons for any slot to see the affected ads (reason <code>no_traffic_excluded_day</code>).
                            </span>
                        }
                    />
                )}
                {availableStrategies.length > 1 && Object.keys(strategyMetrics).length > 0 && (
                    <Card
                        title="Strategy Comparison"
                        style={{ marginBottom: 1, backgroundColor: '#0a0a0a' }}
                        bodyStyle={{ padding: '5px', backgroundColor: '#0a0a0a' }}
                        headStyle={{ backgroundColor: '#0a0a0a', color: 'rgba(255, 255, 255, 0.85)' }}
                    >
                        <Row gutter={16}>
                            {availableStrategies.map(strategy => {
                                const metrics = strategyMetrics[strategy];
                                if (!metrics) return null;

                                const isSelected = strategy === selectedStrategy;
                                const fillRateColor = metrics.overallFillRate >= 95
                                    ? '#52c41a'
                                    : metrics.overallFillRate >= 80
                                        ? '#faad14'
                                        : '#ff4d4f';

                                return (
                                    <Col key={strategy} span={8}>
                                        <Card
                                            size="small"
                                            title={
                                                <Space>
                                                    <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{strategy}</span>
                                                    {isSelected && (
                                                        <Tag
                                                            style={{
                                                                backgroundColor: '#1890ff',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            Currently Viewing
                                                        </Tag>
                                                    )}
                                                </Space>
                                            }
                                            style={{
                                                border: isSelected ? '2px solid #1890ff' : '1px solid #434343',
                                                backgroundColor: isSelected ? '#1a1d24' : '#141414',
                                                cursor: 'pointer'
                                            }}
                                            headStyle={{
                                                backgroundColor: isSelected ? '#1a1d24' : '#141414',
                                                borderBottom: '1px solid #434343'
                                            }}
                                            bodyStyle={{
                                                backgroundColor: isSelected ? '#1a1d24' : '#141414'
                                            }}
                                            onClick={() => setSelectedStrategy(strategy)}
                                            hoverable
                                        >
                                            <Row gutter={[8, 8]}>
                                                <Col span={12}>
                                                    <Statistic
                                                        title={<span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>Fill Rate</span>}
                                                        value={metrics.overallFillRate?.toFixed(2) || 0}
                                                        suffix="%"
                                                        valueStyle={{
                                                            fontSize: '18px',
                                                            fontWeight: 'bold',
                                                            color: fillRateColor
                                                        }}
                                                    />
                                                </Col>
                                                <Col span={12}>
                                                    <Statistic
                                                        title={<span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>Total Value</span>}
                                                        value={metrics.totalDollars || 0}
                                                        prefix="$"
                                                        valueStyle={{
                                                            fontSize: '18px',
                                                            fontWeight: 'bold',
                                                            color: 'rgba(255, 255, 255, 0.85)'
                                                        }}
                                                    />
                                                </Col>
                                                <Col span={12}>
                                                    <Statistic
                                                        title={<span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>Fully Filled</span>}
                                                        value={`${metrics.fullyFilledSlots || 0} / ${metrics.totalSlots || 0}`}
                                                        valueStyle={{
                                                            fontSize: '14px',
                                                            color: 'rgba(255, 255, 255, 0.85)'
                                                        }}
                                                    />
                                                </Col>
                                                <Col span={12}>
                                                    <Statistic
                                                        title={<span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>Unfilled Time</span>}
                                                        value={metrics.totalUnfilledTime || 0}
                                                        suffix="s"
                                                        valueStyle={{
                                                            fontSize: '14px',
                                                            color: metrics.totalUnfilledTime > 0 ? '#ff4d4f' : '#52c41a'
                                                        }}
                                                    />
                                                </Col>
                                                {metrics.durationSeconds != null && metrics.durationSeconds > 0 && (
                                                    <Col span={12}>
                                                        <Statistic
                                                            title={<span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>Duration</span>}
                                                            value={(() => {
                                                                const m = Math.floor(metrics.durationSeconds / 60);
                                                                const s = Math.round(metrics.durationSeconds % 60);
                                                                if (m === 0) return `${s}s`;
                                                                return s > 0 ? `${m}m ${s}s` : `${m}m`;
                                                            })()}
                                                            valueStyle={{
                                                                fontSize: '14px',
                                                                color: 'rgba(255, 255, 255, 0.85)'
                                                            }}
                                                        />
                                                    </Col>
                                                )}
                                                {(() => {
                                                    const { missing, total } = getMissingTrafficForStrategy(strategy);
                                                    return (
                                                        <Col span={12}>
                                                            <Statistic
                                                                title={<span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>Missing Traffic</span>}
                                                                value={`${missing} / ${total}`}
                                                                valueStyle={{
                                                                    fontSize: '14px',
                                                                    color: missing > 0 ? '#ff4d4f' : '#52c41a'
                                                                }}
                                                            />
                                                        </Col>
                                                    );
                                                })()}
                                            </Row>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Card>
                )}
                <Tabs
                    defaultActiveKey="podded-results"
                    items={[
                        {
                            key: 'podded-results',
                            label: 'Podded Results',
                            children: (
                                <SlotTableCore
                                    poddedData={poddedData}
                                    scheduleData={scheduleData}
                                    slotSelectionDict={slotSelectionDict}
                                    setSlotSelectionDict={setSlotSelectionDict}
                                    loading={loading}
                                    getOriginalAdSelectionCounts={getOriginalAdSelectionCounts}
                                    mode="review"
                                    warnings={warnings}
                                    selectedStrategy={selectedStrategy}
                                />
                            )
                        },
                        {
                            key: 'inventory-summary',
                            label: 'Inventory Summary',
                            children: Object.keys(scheduleData).length > 0 ? (
                                <InventorySummaryComponent
                                    scheduleData={scheduleData}
                                    loading={loading}
                                    getAdSelectionCounts={() => getAdSelectionCounts(poddedData, slotSelectionDict)}
                                    getOriginalAdSelectionCounts={getOriginalAdSelectionCounts}
                                />
                            ) : <div>No inventory data available</div>
                        }
                    ]}
                />
            </Space>
        </>
    );
};

export default BatchJobsPoddedReviewComponent;
